import { FormControl, Select, SelectChangeEvent, MenuItem } from "@mui/material";
import React, { useState } from "react";
import { AddApplePayDropDownEntry } from "../../ApplePay/AddApplePayDropdownEntry";
import { InlineCreditCardDropdownEntry } from "../../CreditCard/InlineCreditCardDropdownEntry";
import { AddGooglePayDropDownEntry } from "../../GooglePay/AddGooglePayDropdownEntry";
import { AddPayPalDropDownEntry } from "../../PayPal/AddPayPalDropDownEntry";
import { ContentURL, getContentUrl } from "../../Utils/ContentURL";
import { useAppState } from "../../../Redux/ReduxHooks";
import { PaymentOption } from "../../Payment/PaymentEntities";
import { ExistingCardDropdownEntry } from "./ExistingCardDropdownEntry";

/**
 * Payment selector dropdown. This is used to add a payment method (CNP) to a booking made using a voice channel (e.g. IVR, Contact center).
 */
export const PaymentSelector: React.FC = () => {

    const [paymentType, setPaymentType] = useState('');
    const RemountCounter = useAppState(state => state.uiLogicControl.CardSelectorRemountCounter);
    const errorMessage = useAppState(state => state.payment.PaymentCardError);
    const savedPaymentSelected = useAppState(state => state.PhoneToCnp.PaymentOption?.Card);

    const handlePaymentChange = (event: SelectChangeEvent) => {
        setPaymentType(event.target.value);
    };

    /** Renders a single option. Looks up the card brand icon from the selector (same as
     *  MyWalletTable). Falls back to the generic CreditOrDebit icon if the CardType from
     *  the backend doesn't match a selector key. */
    function RenderSelectOption(option: PaymentOption, index: number) {
        const selector = ContentURL.images.PaymentType.Selector as Record<string, string>;
        const titleCased = option.Type ? option.Type.charAt(0).toUpperCase() + option.Type.slice(1).toLowerCase() : '';
        const icon = selector[option.Type]       // exact match: "Visa" → visa.png
          ?? selector[titleCased]        // title-case fallback: "visa" → "Visa" → visa.png
          ?? ContentURL.images.PaymentType.Selector.CreditOrDebit; // safe fallback, no broken image

        return <MenuItem className="cardMenuItem" key={index} value={option.Id} disableGutters={true}>
            <div className="select-payment-option">
                <img src={getContentUrl(icon)} alt={option.Type + " icon"} />
                <span className="selected-payment-option-name">{option.Name}</span>
            </div>
        </MenuItem>;
    }

    /**
     * Returns the payment option that is currently selected, unless there is an error with it.
     */
    function GetSelectedOption() {
        const option = useAppState(state => state.PhoneToCnp.PaymentOption);
        if (!option) return null;

        // TODO: implement error handling

        return option;
    }

    const selectedOption = GetSelectedOption();

    return (
        <div className="payment-selector" key={RemountCounter}>
            <FormControl fullWidth>
                <Select
                    value={paymentType}
                    onChange={handlePaymentChange}
                    displayEmpty
                    inputProps={{ 'aria-label': 'Without label' }}
                    renderValue={(selected) => {
                        if (selectedOption) {
                            return RenderSelectOption(selectedOption, 0);
                        }
                        else if (selected === '') {
                            return <div className="select-payment-option"><img src={getContentUrl(ContentURL.images.PhoneToCnp.CashIcon)} /><span className="select-payment-method">Select payment method</span></div>
                        }
                        
                        return;
                        
                    }}
                    sx={{
                        borderRadius: '8px',
                        border: '1px solid #E6E6E6',
                        height: '40px',
                        padding: '8px',
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            boxShadow: '0 0 0 3px #FFAC80', // Box shadow for the focused Select
                            transition: 'box-shadow 0.2s ease-in-out',
                            borderColor: '#B23E00',
                        },
                        '&.Mui-focused fieldset': {
                            borderColor: 'transparent !important', // Hide default border on focus
                        },
                        '&.Mui-error .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'transparent !important',
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'transparent !important',
                        },
                        '& .MuiOutlinedInput-input': {
                            padding: '0',                            
                        },
                        'fieldset': {
                            border: 'none',
                        },
                    }}
                >
                    <ExistingCardDropdownEntry />
                    <AddApplePayDropDownEntry />
                    <AddGooglePayDropDownEntry />
                    <AddPayPalDropDownEntry />
                    <InlineCreditCardDropdownEntry />
                </Select>
            </FormControl>
            {errorMessage && (
                <div className="booking-form-error-message">{errorMessage}</div>
            )}
            {savedPaymentSelected && <div className="saved-card-info">You'll use this saved card for your payment hold.</div>}
        </div>
    );
}
